import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import IMAGES from "@/data/images";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

const galleryItems = [
  { key: "renovation-couloir-appartement", legende: "Couloir \u00b7 Rénovation complète \u00b7 Paris" },
  { key: "amenagement-bureau-integre-duplex", legende: "Bureau intégré \u00b7 Aménagement sur mesure \u00b7 Paris" },
  { key: "amenagement-bureaux-verriere", legende: "Bureaux \u00b7 Verrière atelier \u00b7 Paris" },
  { key: "amenagement-entree-banc-integre", legende: "Entrée \u00b7 Placards vert sauge \u00b7 Paris" },
  { key: "bibliotheque-sur-mesure-bois", legende: "Bibliothèque \u00b7 Bois & polycarbonate \u00b7 Paris" },
  { key: "salle-de-bain-carrelage-chevron", legende: "Salle de bain \u00b7 Carrelage chevron \u00b7 Paris" },
  { key: "cuisine-vert-sauge-terrazzo", legende: "Cuisine \u00b7 Vert sauge & terrazzo \u00b7 Paris" },
  { key: "verriere-atelier-bureaux", legende: "Bureaux \u00b7 Verrière atelier \u00b7 Paris" },
  { key: "renovation-entree-escalier-helicoidal", legende: "Hall d'entrée \u00b7 Escalier hélicoïdal \u00b7 Paris" },
  { key: "creation-mezzanine-escalier-bois", legende: "Mezzanine \u00b7 Sur mesure \u00b7 Paris" },
  { key: "cuisine-meubles-vert-sauge-credence", legende: "Cuisine \u00b7 Crédence chevron \u00b7 Paris" },
  { key: "renovation-duplex-cuisine-integree", legende: "Duplex \u00b7 Cuisine intégrée \u00b7 Paris" },
  { key: "detail-main-courante-chene", legende: "Main courante \u00b7 Chêne massif \u00b7 Paris" },
  { key: "cuisine-equipee-carrelage-ciment", legende: "Cuisine \u00b7 Carrelage ciment \u00b7 Paris" },
  { key: "renovation-cuisine-ouverte", legende: "Cuisine ouverte \u00b7 Bar sur mesure \u00b7 Paris" },
  { key: "couloir-parquet-chevron", legende: "Couloir \u00b7 Parquet chevron \u00b7 Paris" },
  { key: "pose-mosaique-douche-italienne", legende: "Douche \u00b7 Mosaïque \u00b7 Paris" },
  { key: "placards-suspendus-mur-accent-bleu", legende: "Rangements \u00b7 Mur accent \u00b7 Paris" },
  { key: "pose-porte-interieure-chene", legende: "Menuiserie \u00b7 Porte intérieure \u00b7 Paris" },
];

export default function Realisations() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = useCallback((index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const slides = galleryItems.map((item) => ({
    src: IMAGES[item.key].url,
    alt: IMAGES[item.key].alt,
  }));

  return (
    <>
      <SEO
        title="Réalisations — Rénovation appartements et bureaux à Paris | Bati Rui"
        description="Découvrez nos chantiers réalisés à Paris : appartements haussmanniens, salles de bain, cuisines sur mesure, bureaux professionnels. Bati Rui depuis 2011."
        canonical="https://www.batirui.com/realisations"
      />

      {/* Introduction */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-[#F8F6F2]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1
            data-testid="realisations-h1"
            className="font-heading text-4xl sm:text-5xl font-black text-[#1B2E4B] tracking-tight"
          >
            Nos réalisations — Paris & Ile-de-France
          </h1>
          <p className="mt-6 text-[#3D3D3A] text-base sm:text-lg leading-relaxed max-w-3xl">
            Chaque chantier est unique. Voici une sélection de projets réalisés
            par nos équipes à Paris et en région parisienne — rénovations
            complètes d'appartements, aménagements sur mesure et espaces professionnels.
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section data-testid="gallery-section" className="pb-20 md:pb-32 bg-[#F8F6F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryItems.map((item, index) => (
              <button
                key={item.key}
                data-testid={`gallery-item-${index}`}
                onClick={() => openLightbox(index)}
                className="group text-left cursor-pointer"
              >
                <div className="overflow-hidden rounded-md bg-[#EEECE8]">
                  <img
                    src={IMAGES[item.key].url}
                    alt={IMAGES[item.key].alt}
                    className="w-full object-contain"
                    loading="lazy"
                  />
                </div>
                <p className="mt-2 text-sm text-[#3D3D3A]">{item.legende}</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={slides}
      />

      {/* CTA */}
      <section className="py-20 md:py-32 bg-[#1B2E4B]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight">
            Vous souhaitez un résultat similaire ?
          </h2>
          <Link
            to="/contact"
            data-testid="realisations-cta-devis"
            className="inline-block mt-10 bg-[#C0392B] hover:bg-[#a02f23] text-white rounded-md px-8 py-4 text-base font-medium transition-colors"
          >
            Demander un devis gratuit
          </Link>
        </div>
      </section>
    </>
  );
}
